import { z } from 'zod';
import { codiceFiscalePFSchema, codiceFiscalePGSchema } from './codiceFiscale.js';
import { ibanSchema } from './iban.js';
/**
 * Billing Profile — profilo di fatturazione di un Account.
 *
 * Modellato come Zod discriminated union su `type: 'PF' | 'PG'`:
 *  - PF (persona fisica): firstName, lastName, codiceFiscale (16 char)
 *  - PG (persona giuridica): ragioneSociale, piva (11 cifre, MOD 11),
 *    sdiCode (7 alfanum uppercase) oppure sdiPec (email valida) — uno dei due obbligatorio.
 *
 * Entrambi condividono il blocco indirizzo (indirizzo, cap, citta, provincia, nazione)
 * e un IBAN opzionale (in DB memorizzato cifrato in `ibanEncrypted`).
 */
/* -------------------------------------------------------------------------- */
/* Indirizzo (comune PF/PG)                                                   */
/* -------------------------------------------------------------------------- */
const capItalianoSchema = z
    .string({ required_error: 'CAP obbligatorio' })
    .trim()
    .regex(/^[0-9]{5}$/, { message: 'CAP deve essere di 5 cifre numeriche' });
const provinciaItalianaSchema = z
    .string({ required_error: 'Provincia obbligatoria' })
    .trim()
    .transform((v) => v.toUpperCase())
    .refine((v) => /^[A-Z]{2}$/.test(v), { message: 'Provincia deve essere sigla di 2 lettere' });
const nazioneSchema = z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .refine((v) => /^[A-Z]{2}$/.test(v), { message: "Codice nazione ISO 3166-1 alpha-2 (2 lettere)" })
    .default('IT');
const indirizzoBaseSchema = z.object({
    indirizzo: z
        .string({ required_error: 'Indirizzo obbligatorio' })
        .trim()
        .min(3, { message: 'Indirizzo troppo corto' })
        .max(200, { message: 'Indirizzo troppo lungo' }),
    cap: capItalianoSchema,
    citta: z
        .string({ required_error: 'Città obbligatoria' })
        .trim()
        .min(1, { message: 'Città obbligatoria' })
        .max(100),
    provincia: provinciaItalianaSchema,
    nazione: nazioneSchema,
});
/* -------------------------------------------------------------------------- */
/* SDI — codice destinatario / PEC                                             */
/* -------------------------------------------------------------------------- */
/** Codice SDI destinatario: 7 caratteri alfanumerici uppercase (es. "0000000" per PEC). */
export const sdiCodeSchema = z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .refine((v) => /^[A-Z0-9]{7}$/.test(v), {
    message: 'Codice SDI deve essere di 7 caratteri alfanumerici',
});
/** PEC SDI: email RFC 5322 minimale — la verifica di dominio PEC è fuori scope V1. */
export const sdiPecSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email({ message: 'PEC SDI deve essere un indirizzo email valido' });
/* -------------------------------------------------------------------------- */
/* PF — persona fisica                                                         */
/* -------------------------------------------------------------------------- */
const billingProfilePFSchema = z
    .object({
    type: z.literal('PF'),
    firstName: z
        .string({ required_error: 'Nome obbligatorio' })
        .trim()
        .min(1, { message: 'Nome obbligatorio' })
        .max(100),
    lastName: z
        .string({ required_error: 'Cognome obbligatorio' })
        .trim()
        .min(1, { message: 'Cognome obbligatorio' })
        .max(100),
    codiceFiscale: codiceFiscalePFSchema,
    ibanEncrypted: z.string().optional(),
    iban: ibanSchema.optional(),
})
    .merge(indirizzoBaseSchema);
/* -------------------------------------------------------------------------- */
/* PG — persona giuridica                                                      */
/* -------------------------------------------------------------------------- */
const billingProfilePGSchema = z
    .object({
    type: z.literal('PG'),
    ragioneSociale: z
        .string({ required_error: 'Ragione sociale obbligatoria' })
        .trim()
        .min(1, { message: 'Ragione sociale obbligatoria' })
        .max(200),
    piva: codiceFiscalePGSchema,
    sdiCode: sdiCodeSchema.optional(),
    sdiPec: sdiPecSchema.optional(),
    ibanEncrypted: z.string().optional(),
    iban: ibanSchema.optional(),
})
    .merge(indirizzoBaseSchema)
    .superRefine((data, ctx) => {
    // Almeno uno tra sdiCode e sdiPec deve essere presente.
    if (!data.sdiCode && !data.sdiPec) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sdiCode'],
            message: "Per i soggetti PG è richiesto almeno uno tra 'sdiCode' o 'sdiPec'",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Discriminated union                                                         */
/* -------------------------------------------------------------------------- */
/**
 * BillingProfile schema pubblico (discriminated union su `type`).
 *
 * Nota: `z.discriminatedUnion` non supporta `ZodEffects` come branch,
 * quindi usiamo `z.union` + `superRefine` di smistamento manuale.
 */
export const billingProfileSchema = z.union([billingProfilePFSchema, billingProfilePGSchema]);
export { billingProfilePFSchema, billingProfilePGSchema };
/* -------------------------------------------------------------------------- */
/* F-002: paymentMethodPreference enum                                        */
/* -------------------------------------------------------------------------- */
/**
 * Preferenza metodo di pagamento dell'account.
 *  - WALLET: addebito automatico sul wallet pre-pagato.
 *  - PAY_PER_PRACTICE: pagamento puntuale per ogni pratica (Stripe).
 *
 * Stored as nullable string in DB; validated here at application boundary.
 */
export const paymentMethodPreferenceSchema = z.enum(['WALLET', 'PAY_PER_PRACTICE']);
/* -------------------------------------------------------------------------- */
/* F-002: upsert input schema (strict, anti mass-assignment)                  */
/* -------------------------------------------------------------------------- */
/**
 * Schema per input upsert PUT /api/v1/billing-profile.
 *
 * Differenze rispetto a `billingProfileSchema`:
 *  - `.strict()` per anti-mass-assignment: rifiuta campi extra.
 *  - NON include `ibanEncrypted` (campo server-side, popolato dal service
 *    dopo encrypt). Il client può inviare `iban` plaintext opzionale.
 *  - Include `paymentMethodPreference` opzionale.
 *
 * Le branch PF/PG ereditano la validazione (CF/PIVA + indirizzo + sdi) dai
 * sotto-schemi esistenti, replicandone i campi con `.strict()` per evitare
 * il drift causato da `superRefine` su `z.union`.
 */
const billingProfileUpsertPFSchema = z
    .object({
    type: z.literal('PF'),
    firstName: z
        .string({ required_error: 'Nome obbligatorio' })
        .trim()
        .min(1, { message: 'Nome obbligatorio' })
        .max(100),
    lastName: z
        .string({ required_error: 'Cognome obbligatorio' })
        .trim()
        .min(1, { message: 'Cognome obbligatorio' })
        .max(100),
    codiceFiscale: codiceFiscalePFSchema,
    iban: ibanSchema.optional(),
    paymentMethodPreference: paymentMethodPreferenceSchema.optional(),
})
    .merge(indirizzoBaseSchema)
    .strict();
const billingProfileUpsertPGSchema = z
    .object({
    type: z.literal('PG'),
    ragioneSociale: z
        .string({ required_error: 'Ragione sociale obbligatoria' })
        .trim()
        .min(1, { message: 'Ragione sociale obbligatoria' })
        .max(200),
    piva: codiceFiscalePGSchema,
    sdiCode: sdiCodeSchema.optional(),
    sdiPec: sdiPecSchema.optional(),
    iban: ibanSchema.optional(),
    paymentMethodPreference: paymentMethodPreferenceSchema.optional(),
})
    .merge(indirizzoBaseSchema)
    .strict()
    .superRefine((data, ctx) => {
    if (!data.sdiCode && !data.sdiPec) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sdiCode'],
            message: "Per i soggetti PG è richiesto almeno uno tra 'sdiCode' o 'sdiPec'",
        });
    }
});
export const billingProfileUpsertInputSchema = z.union([
    billingProfileUpsertPFSchema,
    billingProfileUpsertPGSchema,
]);
export { billingProfileUpsertPFSchema, billingProfileUpsertPGSchema };
/**
 * Predicato puro: ritorna `true` se il profilo ha tutti i campi anagrafici
 * minimi richiesti per essere considerato completo (gating F-003/F-011).
 *
 * Pure function — niente side effect, niente IO. Riusato dal middleware
 * `requireCompleteBillingProfile` (api) e dal `ProfileCompletenessBadge`
 * (web).
 */
export function isBillingProfileComplete(profile) {
    if (!profile)
        return false;
    const hasAddress = Boolean(profile.indirizzo &&
        profile.cap &&
        profile.citta &&
        profile.provincia);
    if (!hasAddress)
        return false;
    if (profile.type === 'PF') {
        return Boolean(profile.firstName && profile.lastName && profile.codiceFiscale);
    }
    if (profile.type === 'PG') {
        const hasSdi = Boolean(profile.sdiCode || profile.sdiPec);
        return Boolean(profile.ragioneSociale && profile.piva && hasSdi);
    }
    return false;
}
//# sourceMappingURL=billingProfile.js.map